import React, { useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import { Mail, MessageSquare, Phone, MapPin, Loader2, CheckCircle2 } from 'lucide-react';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', subject: 'General inquiry', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!form.name || !form.email.includes('@') || !form.message) {
      toast.error('Name, valid email, and message are required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/marketing/contact', form);
      setSent(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <PublicNav />

      <section className="max-w-5xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <span className="inline-block text-[10px] font-bold px-3 py-1 rounded-full bg-[#D4A843]/15 text-[#D4A843] uppercase tracking-wider mb-4">Contact</span>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Let's talk.</h1>
          <p className="text-gray-400 max-w-xl mx-auto">Sales, support, partnerships, or just a question — we read every message.</p>
        </div>

        <div className="grid md:grid-cols-5 gap-8">
          <div className="md:col-span-2 space-y-5">
            <div className="bg-[#111] border border-gray-800 rounded-2xl p-5">
              <Mail className="w-5 h-5 text-[#D4A843] mb-2" />
              <p className="text-xs text-gray-500 uppercase tracking-wide">Email</p>
              <a href="mailto:sales@eigooinc.net" className="text-sm text-white hover:text-[#D4A843]">sales@eigooinc.net</a>
            </div>
            <div className="bg-[#111] border border-gray-800 rounded-2xl p-5">
              <MessageSquare className="w-5 h-5 text-[#D4A843] mb-2" />
              <p className="text-xs text-gray-500 uppercase tracking-wide">Support</p>
              <a href="mailto:sales@eigooinc.net" className="text-sm text-white hover:text-[#D4A843]">sales@eigooinc.net</a>
              <p className="text-[10px] text-gray-500 mt-1">Typically respond within 4 business hours</p>
            </div>
            <div className="bg-[#111] border border-gray-800 rounded-2xl p-5">
              <MapPin className="w-5 h-5 text-[#D4A843] mb-2" />
              <p className="text-xs text-gray-500 uppercase tracking-wide">Headquarters</p>
              <p className="text-sm text-white">EIGOO Inc.</p>
              <p className="text-sm text-gray-400">Hagåtña, Guam USA</p>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="bg-[#111] border border-gray-800 rounded-2xl p-6">
              {sent ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <div className="w-14 h-14 bg-[#D4A843] rounded-2xl mx-auto mb-4 flex items-center justify-center">
                    <CheckCircle2 className="w-7 h-7 text-[#0A0A0A]" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Thanks, {form.name.split(' ')[0]}!</h3>
                  <p className="text-sm text-gray-400">Your message is in our inbox. You'll hear back at <span className="text-white">{form.email}</span> soon.</p>
                  <Button onClick={() => { setSent(false); setForm({ name: '', email: '', phone: '', company: '', subject: 'General inquiry', message: '' }); }} variant="outline" className="mt-6 border-gray-700 text-white rounded-lg" data-testid="contact-reset">Send another</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs text-gray-400">Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white" data-testid="contact-name" /></div>
                    <div><Label className="text-xs text-gray-400">Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white" data-testid="contact-email" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs text-gray-400">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white" /></div>
                    <div><Label className="text-xs text-gray-400">Company</Label><Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white" /></div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">Subject</Label>
                    <select value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} className="mt-1 w-full h-9 rounded-lg bg-[#0A0A0A] border border-gray-700 text-white px-3 text-xs" data-testid="contact-subject">
                      <option>General inquiry</option>
                      <option>Sales — request a demo</option>
                      <option>Partnership / reseller</option>
                      <option>Press / media</option>
                      <option>Support / existing customer</option>
                      <option>Freelancer application</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">Message *</Label>
                    <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="mt-1 w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white min-h-[140px]" placeholder="Tell us what you need..." data-testid="contact-message" />
                  </div>
                  <Button onClick={submit} disabled={submitting} className="w-full bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-bold rounded-lg py-3" data-testid="contact-submit">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send message'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
